(() => {
  const moneyFormat = (cents, format) => {
    const value = Number(cents || 0) / 100;
    const formatted = value.toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return format && format.includes('{{amount}}') ? format.replace('{{amount}}', formatted.replace(/^[^\d-]+/, '')) : formatted;
  };

  class PeachyProductPage extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-peachy-product-form]');
      this.quantityInput = this.querySelector('[data-peachy-quantity-input]');
      this.variantInput = this.querySelector('[data-peachy-variant-id]');
      this.priceBox = this.querySelector('[data-peachy-pack-price]');
      this.mainPrice = this.querySelector('[data-peachy-main-price]');
      this.originalPrice = this.querySelector('[data-peachy-original-price]');
      this.saveLabel = this.querySelector('[data-peachy-save-label]');
      this.packLabel = this.querySelector('[data-peachy-pack-label]');
      this.checkoutButton = this.querySelector('[data-peachy-checkout-button]');
      this.defaultVariantId = this.variantInput?.value;
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.unitPrice = Number(this.dataset.unitPrice || 0);
      this.unitComparePrice = Number(this.dataset.unitComparePrice || 0);
      this.tiers = Array.from(this.querySelectorAll('[data-peachy-tier]')).map((node) => ({
        quantity: Number(node.dataset.quantity),
        variantId: node.dataset.variantId,
        price: Number(node.dataset.price),
        compare: Number(node.dataset.compare || 0),
        label: node.dataset.label || `${node.dataset.quantity} Pack`
      })).filter((tier) => tier.quantity && tier.variantId && tier.price);

      this.bindQuantity();
      this.bindGallery();
      this.bindFaqs();
      this.updatePricing();

      this.form?.addEventListener('submit', (event) => this.submitForm(event));
      this.checkoutButton?.addEventListener('click', () => this.prepareCheckout());
    }

    bindQuantity() {
      this.querySelectorAll('[data-peachy-quantity-button]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.peachyQuantityButton === 'plus' ? 1 : -1;
          const current = Number(this.quantityInput?.value || 1);
          const next = Math.max(1, Math.min(99, current + direction));
          if (this.quantityInput) {
            this.quantityInput.value = String(next);
            this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });

      this.quantityInput?.addEventListener('input', () => this.updatePricing());
      this.quantityInput?.addEventListener('change', () => this.updatePricing());
    }

    bindGallery() {
      const stage = this.querySelector('[data-peachy-gallery-stage]');
      this.querySelectorAll('[data-peachy-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const image = thumb.querySelector('img');
          if (!stage || !image) return;
          stage.src = image.dataset.fullSrc || image.src;
          stage.alt = image.alt || stage.alt;
          this.querySelectorAll('[data-peachy-gallery-thumb]').forEach((item) => item.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    }

    bindFaqs() {
      this.querySelectorAll('[data-peachy-faq-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          const item = button.closest('.peachy-faq__item');
          const expanded = item?.classList.toggle('is-open');
          button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
      });
    }

    findTier(quantity) {
      return this.tiers.find((tier) => tier.quantity === quantity);
    }

    currentQuantity() {
      return Math.max(1, Number(this.quantityInput?.value || 1));
    }

    priceState(quantity = this.currentQuantity()) {
      const tier = this.findTier(quantity);
      if (tier) {
        const baseline = tier.compare > tier.price ? tier.compare : this.unitPrice * quantity;
        return {
          quantity,
          variantId: tier.variantId,
          cartQuantity: 1,
          price: tier.price,
          compare: baseline,
          label: tier.label
        };
      }
      return {
        quantity,
        variantId: this.defaultVariantId,
        cartQuantity: quantity,
        price: this.unitPrice * quantity,
        compare: (this.unitComparePrice > this.unitPrice ? this.unitComparePrice : this.unitPrice) * quantity,
        label: `${quantity} Pack`
      };
    }

    updatePricing() {
      const state = this.priceState();
      const saved = Math.max(0, state.compare - state.price);
      const percent = state.compare > state.price ? Math.round((saved / state.compare) * 100) : 0;

      if (this.mainPrice) this.mainPrice.textContent = moneyFormat(state.price, this.moneyFormat);
      if (this.originalPrice) {
        this.originalPrice.textContent = state.compare > state.price ? moneyFormat(state.compare, this.moneyFormat) : '';
        this.originalPrice.hidden = state.compare <= state.price;
      }
      if (this.saveLabel) {
        this.saveLabel.textContent = percent > 0 ? `Save ${percent}%` : 'Best daily value';
      }
      if (this.packLabel) this.packLabel.textContent = state.label;
      if (this.variantInput) this.variantInput.value = state.variantId || this.defaultVariantId;
      if (this.checkoutButton && state.variantId) {
        this.checkoutButton.href = `/cart/${state.variantId}:${state.cartQuantity}?checkout`;
      }

      this.dataset.cartQuantity = String(state.cartQuantity);
    }

    prepareCheckout() {
      this.updatePricing();
      const state = this.priceState();
      if (this.variantInput) this.variantInput.value = state.variantId || this.defaultVariantId;
      const quantity = this.form?.querySelector('[name="quantity"]');
      if (quantity) quantity.value = String(state.cartQuantity);
    }

    async submitForm(event) {
      if (!this.form) return;
      this.updatePricing();
      const state = this.priceState();
      const quantity = this.form.querySelector('[name="quantity"]');
      const button = this.form.querySelector('[type="submit"]');

      if (quantity) quantity.value = String(state.cartQuantity);
      if (!window.fetch || this.form.dataset.peachyAjax === 'false') return;

      event.preventDefault();
      const originalText = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = 'Adding...';
      }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form)
        });
        if (!response.ok) throw new Error('Add to cart failed');
        if (button) button.textContent = 'Added';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        setTimeout(() => {
          if (button) button.textContent = originalText || 'Add to cart';
        }, 1400);
      } catch (error) {
        this.form.dataset.peachyAjax = 'false';
        this.form.submit();
      } finally {
        if (button) button.disabled = false;
      }
    }
  }

  const bindGlobalFaqs = () => {
    document.querySelectorAll('[data-peachy-faq-toggle]:not([data-peachy-bound])').forEach((button) => {
      button.dataset.peachyBound = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('.peachy-faq__item');
        const expanded = item?.classList.toggle('is-open');
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  };

  if (!customElements.get('peachy-product-page')) {
    customElements.define('peachy-product-page', PeachyProductPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindGlobalFaqs);
  } else {
    bindGlobalFaqs();
  }
})();
